import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames/bind';

import { getSizeRecommendationConfig } from '../../selectors/featureFlagsSelector';
import { readCookie } from '../../../components/common/Cookie';
import styles from './sizeRecommendation.pcss';
import { getCurrentStore } from '../../selectors/metaData';
import { segmentEventPush } from '../../../modules/ga/GTMdatalayer';
import { getProductDetails } from '../../selectors/productpage';
import { getProperty } from '../../../modules/ajio-util/properties';

declare global {
  interface Window {
    SizeSenseLoader?: {
      createDrawer: (containerId: string, options: any) => void;
    };
    SizeSenseDrawerHandler: {
      open: () => void;
      close: () => void;
    };
  }
}

const cx = classnames.bind(styles);

const SIZE_STATUS_ENUMS_MAP = {
  SUCCESS: 'recommendedSizeText',
  FAILED: 'tryAgaingText',
  NO_RECOMMENDATION: 'noSizeRecommendedText',
  NO_PREFERENCE: 'findYourSizeText',
  OFFLINE: 'toolOfflineText'
} as const;

const SIZE_STATUS_ENUMS = {
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  NO_RECOMMENDATION: 'NO_RECOMMENDATION',
  NO_PREFERENCE: 'NO_PREFERENCE',
  OFFLINE: 'OFFLINE'
} as const;

interface SizeRecommendationConfig {
  enable?: string;
  texts?: Record<string, string>;
  iconUrls?: Record<string, string>;
  sizeConfig?: Array<{
    segment?: string;
    vertical?: string;
    brick?: string;
  }>;
  storeType?: string[];
  accessToken?: string;
  environment?: string;
}

interface SizeRecommendFetch {
  fit?: string;
}

interface ProductDetails {
  name?: string;
  code?: string;
  wasPriceData?: { value?: number };
  price?: { value?: number };
}

interface RecommendationData {
  size?: string;
  confidence_score?: string;
}

interface SizeItem {
  value?: string;
}

interface SizeRecommendationProps {
  isDesktop?: boolean;
  isFromSizeChart?: boolean;
  sizeRecommendationDialogAjiogram?: boolean;
  fromLuxeDescription?: boolean;
  brandName?: string;
  brickName?: string;
  brickCategory?: string;
  brickSubCategory?: string;
  currentStore?: string;
  sizeRecommendationConfig?: SizeRecommendationConfig;
  sizeRecommendFetch?: SizeRecommendFetch;
  productDetails?: ProductDetails;
  sizes?: SizeItem[];
  hideSizeDialog?: () => void;
}

interface State {
  flags?: any;
  [key: string]: any;
}

const SizeRecommendation = (props: SizeRecommendationProps) => {
  const {
    isDesktop = false,
    isFromSizeChart = false,
    sizeRecommendationDialogAjiogram = false,
    fromLuxeDescription = false,
    brandName = '',
    brickName = '',
    brickCategory = '',
    brickSubCategory = '',
    currentStore = 'ajio',
    sizeRecommendationConfig = {},
    sizeRecommendFetch = {},
    productDetails = {},
    sizes = [],
    hideSizeDialog
  } = props;
  const { enable = 'OFF', texts = {}, iconUrls = {}, sizeConfig = [], storeType = [] } =
    sizeRecommendationConfig || {};
  const sizeSenseConfig = getProperty('sizeSenseConfig');
  const { accessToken = '', environment = '' } = sizeSenseConfig || {};
  const [sizeStatus, setSizeStatus] = useState('');
  const [recommendationData, setRecommendationData] = useState<RecommendationData>({});
  const recommendationContainerRef = useRef(null);
  const viewEventFired = useRef(false);
  const recommendationCount = useRef(0);
  const drawerInitialized = useRef(false);
  const user_id = readCookie('CI') || '';

  console.log('sizeStatus', sizeStatus);

  // NOTE: WIDGET SHOW/HIDE CONDITION
  let shouldShowSizeRecommendation =
    enable === 'ON' && user_id !== '' &&
    storeType.includes((currentStore || 'ajio')?.toLowerCase()) &&
    sizeConfig?.find(el => {
      // Segment is mandatory
      if (!el?.segment || el.segment !== brickCategory) return false;

      // Check for the three possible combinations:
      // 1. {Segment + Vertical + Brick}
      if (el.vertical && el.brick) {
        return el.vertical === brickSubCategory && el.brick === brickName;
      }

      // 2. {Segment + Vertical} (brick is not specified)
      if (el.vertical && !el.brick) {
        return el.vertical === brickSubCategory;
      }

      // 3. {Segment + Brick} (vertical is not specified)
      if (!el.vertical && el.brick) {
        return el.brick === brickName;
      }

      // 4. {Segment only} - if neither vertical nor brick is specified
      return !el.vertical && !el.brick;
    });

  // shouldShowSizeRecommendation = true; // for testing

  const getRecommendationMsg = (sizeStatus: keyof typeof SIZE_STATUS_ENUMS_MAP) => {
    const text =
      texts[SIZE_STATUS_ENUMS_MAP[sizeStatus as keyof typeof SIZE_STATUS_ENUMS_MAP]] || '';

    return (() => {
      if (sizeStatus === SIZE_STATUS_ENUMS.SUCCESS) {
        return `${texts.sizeText?.replace(
          '${size}',
          (recommendationData as any)?.size || ''
        )} ${text} ${texts.editCTA}`.trim();
      }

      if (sizeStatus === SIZE_STATUS_ENUMS.FAILED) {
        return `${text} ${texts.tryAgainCTA}`.trim();
      }

      if (
        [
          SIZE_STATUS_ENUMS.NO_PREFERENCE,
          SIZE_STATUS_ENUMS.NO_RECOMMENDATION,
          SIZE_STATUS_ENUMS.OFFLINE
        ].includes(sizeStatus)
      ) {
        return text;
      }

      return '';
    })();
  };

  const getProductData = (params: { shouldPassSdkParams?: boolean }) => {
    const { name = '', code = '', wasPriceData = {}, price = {} } = productDetails || {};
    const { shouldPassSdkParams } = params;

    return {
      product_id: code,
      product_name: name,
      product_brick: brickName,
      product_vertical: brickSubCategory,
      product_brand: brandName,
      product_list_price: wasPriceData?.value ?? 0,
      product_mrp: price?.value ?? 0,
      ...(shouldPassSdkParams && {
        recommended_size: recommendationData?.size ?? '',
        confidence_score: recommendationData?.confidence_score ?? ''
      })
    };
  };

  const fireCTASegment = ({ isEditPreferenceCTAClicked = false }) => {
    segmentEventPush({
      event: isEditPreferenceCTAClicked
        ? 'Ajio Sizefinder Edit Preferences Clicked'
        : 'Ajio Sizefinder CTA Clicked',
      page_name: isFromSizeChart ? 'size selection half cut screen' : 'pdp screen',
      products: [getProductData({ shouldPassSdkParams: isEditPreferenceCTAClicked })]
    });
  };

  useEffect(() => {
    const { fit = '' } = sizeRecommendFetch || {};

    if (!shouldShowSizeRecommendation) return;

    // Prevent multiple initializations
    if (drawerInitialized.current) return;

    // Function to initialize the drawer
    const initializeDrawer = () => {
      if (!window.SizeSenseLoader) {
        console.error('SizeSenseLoader is not available');
        setSizeStatus('OFFLINE');
        return;
      }

      // Mark as initialized to prevent re-initialization
      drawerInitialized.current = true;

      window.SizeSenseLoader.createDrawer(
        isFromSizeChart ? 'sizechart-drawer-container' : 'drawer-container',
        {
          user_id: readCookie('CI') || '',
          target_brand: brandName || '',
          fit_type: fit,
          category: brickSubCategory || '',
          sub_category: brickName || '',
          gender: brickCategory || '', // Men, Women, Boys
          store: currentStore.toUpperCase() || 'AJIO',
          channel: isDesktop ? 'WEB' : 'MSITE',
          available_sizes: (sizes ?? [])?.map((el: SizeItem) => el?.value).filter(Boolean),
          onUserDrop: (step: string) => {
            console.log('User dropped at step:', step);
          },
          onStepChange: (step: string) => {
            console.log('Step changed to:', step);
          },
          onRecommendation: (recommendation: any, status: any) => {
            console.log('Recommendation:', {
              recommendation,
              status
            });
            setSizeStatus(status);
            setRecommendationData(recommendation.data);
            recommendationCount.current += 1;
          },
          access_token: accessToken,
          environment,
          onWidgetOpen: () => {
            console.log('Widget opened');
            segmentEventPush({
              event: 'Screen Viewed',
              screen_name: 'Sizefinder Screen'
            });
          },
          onWidgetClose: () => {
            console.log('Widget closed');
            segmentEventPush({
              event: 'Ajio Sizefinder Modal Closed',
              products: [getProductData({ shouldPassSdkParams: false })]
            });
          }
        }
      );
    };

    // Try to initialize immediately
    if (window.SizeSenseLoader) {
      initializeDrawer();
    } else {
      // If SDK is not loaded, wait for it with a timeout
      let attempts = 0;
      const maxAttempts = 3; // Wait up to 5 seconds (10 * 500ms)

      const checkSDK = () => {
        attempts++;
        if (window.SizeSenseLoader) {
          initializeDrawer();
        } else if (attempts < maxAttempts) {
          setTimeout(checkSDK, 500);
        } else {
          console.error('SizeSenseLoader failed to load after 3 seconds');
          setSizeStatus('OFFLINE');
        }
      };

      setTimeout(checkSDK, 500);
    }
  }, [
    shouldShowSizeRecommendation,
    sizeRecommendationConfig,
    sizeRecommendFetch,
    isFromSizeChart,
    brandName,
    brickSubCategory,
    brickName,
    brickCategory,
    currentStore,
    isDesktop,
    sizes,
    environment,
    accessToken
  ]);

  // NOTE: FOR Segment
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          shouldShowSizeRecommendation &&
          sizeStatus &&
          !viewEventFired.current
        ) {
          segmentEventPush({
            event: 'Ajio Sizefinder CTA Viewed',
            page_name: isFromSizeChart ? 'size selection half cut screen' : 'pdp screen',
            recommendation_message: getRecommendationMsg(
              sizeStatus as keyof typeof SIZE_STATUS_ENUMS_MAP
            ),
            products: [getProductData({ shouldPassSdkParams: true })]
          });

          viewEventFired.current = true;
          observer.disconnect(); // Optional: stop observing after first trigger
        }
      },
      {
        threshold: 1
      }
    );

    if (recommendationContainerRef?.current) {
      observer.observe(recommendationContainerRef?.current);
    }

    return () => {
      if (recommendationContainerRef?.current) {
        observer.unobserve(recommendationContainerRef?.current);
      }
    };
  }, [recommendationContainerRef?.current, sizeStatus]);

  // NOTE: FOR Segment
  useEffect(() => {
    if (
      !shouldShowSizeRecommendation ||
      !Object.keys(recommendationData).length ||
      recommendationCount.current <= 1
    ) {
      return;
    }

    segmentEventPush({
      event: 'Ajio Sizefinder Input Completed',
      screen_name: 'pdp screen',
      products: [getProductData({ shouldPassSdkParams: true })]
    });
  }, [recommendationData]);

  const ctaUI = (sizeStatus: string, className: string[] = []) => {
    const text =
      texts[SIZE_STATUS_ENUMS_MAP[sizeStatus as keyof typeof SIZE_STATUS_ENUMS_MAP]] || '';
    if (sizeStatus === SIZE_STATUS_ENUMS.SUCCESS) {
      return (
        <>
          <span className={cx('size', className)}>
            {texts.sizeText?.replace('${size}', (recommendationData as any)?.size || '')}
          </span>{' '}
          <span className={cx('sizeTest', className)}> {text} </span>
          <button
            className={cx('btn', className)}
            onClick={() => {
              if (hideSizeDialog) {
                hideSizeDialog();
              }
              fireCTASegment({ isEditPreferenceCTAClicked: true });
              window.SizeSenseDrawerHandler?.open();
            }}
          >
            {texts.editCTA}
          </button>
        </>
      );
    } else if (sizeStatus === SIZE_STATUS_ENUMS.FAILED) {
      return (
        <>
          <span className={cx('sizeTest', className)}> {text} </span>
          <button
            className={cx('btn', className)}
            onClick={() => {
              if (hideSizeDialog) {
                hideSizeDialog();
              }
              window.SizeSenseDrawerHandler?.open();
            }}
          >
            {' '}
            {texts.tryAgainCTA}
          </button>
        </>
      );
    } else if (sizeStatus === SIZE_STATUS_ENUMS.NO_PREFERENCE) {
      return (
        <button
          className={cx('btn', className)}
          onClick={() => {
            if (hideSizeDialog) {
              hideSizeDialog();
            }
            fireCTASegment({});
            window.SizeSenseDrawerHandler?.open();
          }}
        >
          {text}
        </button>
      );
    } else if (
      sizeStatus === SIZE_STATUS_ENUMS.NO_RECOMMENDATION ||
      sizeStatus === SIZE_STATUS_ENUMS.OFFLINE
    ) {
      return <div className={cx('sizeTest', className)}>{text}</div>;
    }

    return <div></div>;
  };

  const storeConfig = (() => {
    if (currentStore === 'ajiogram') {
      return {
        class: 'ajiogram',
        iconUrl: iconUrls?.ajiogram
      };
    }

    if (currentStore === 'luxe') {
      return {
        class: 'luxe',
        iconUrl: iconUrls?.luxe
      };
    }

    return {
      class: '',
      iconUrl: iconUrls?.ajio
    };
  })();

  const isAjiogramPopup = sizeRecommendationDialogAjiogram ? 'ajiogram_popup' : '';
  const isFromLuxeDescription = fromLuxeDescription ? 'luxe_description' : '';
  const desktopClass = isDesktop ? 'desktop' : '';

  if (!shouldShowSizeRecommendation) return null;

  return (
    <>
      <div id={isFromSizeChart ? 'sizechart-drawer-container' : 'drawer-container'}></div>
      <div
        className={cx(
          'size_recommendation-container',
          storeConfig.class,
          isAjiogramPopup,
          isFromLuxeDescription,
          desktopClass
        )}
        ref={recommendationContainerRef}
      >
        {sizeStatus && <img className={cx('image', desktopClass)} src={storeConfig.iconUrl} />}
        <div className={cx('size_details', storeConfig.class)}>
          {ctaUI(sizeStatus, [storeConfig.class, desktopClass])}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: State) => {
  const { flags = {} } = state;
  return {
    sizeRecommendationConfig: getSizeRecommendationConfig(flags),
    currentStore: getCurrentStore(state),
    productDetails: getProductDetails(state)
  };
};
const connector = connect(mapStateToProps);
export default connector(SizeRecommendation);
